import { ROLES } from '@/constants/roles';

/**
 * Between context block and user reply in a single `body`. No angle brackets — some stacks
 * strip `<<<...>>>` (shows as `<<>>`), which broke splitting.
 */
export const CHAT_REPLY_CONTEXT_SEPARATOR = '\n__COP32_REPLY_BODY__\n';

/** @type {string[]} oldest last; split uses first match */
const CHAT_REPLY_SPLIT_MARKERS = [
    CHAT_REPLY_CONTEXT_SEPARATOR,
    '\n<<<COP32_REPLY_CONTEXT_END>>>\n',
    '\n<<>>\n',
    '<<>>',
];

/**
 * @param {string} [body]
 * @returns {{ context: string | null, reply: string | null }}
 */
export function splitChatMessageBody(body) {
    if (typeof body !== 'string') {
        return { context: null, reply: '' };
    }
    let sep = '';
    let i = -1;
    for (const marker of CHAT_REPLY_SPLIT_MARKERS) {
        const idx = body.indexOf(marker);
        if (idx !== -1 && (i === -1 || idx < i)) {
            i = idx;
            sep = marker;
        }
    }
    if (i === -1 || !sep) {
        return { context: null, reply: body };
    }
    const context = body.slice(0, i).trimEnd();
    const reply = body.slice(i + sep.length).trim();
    return {
        context: context.length > 0 ? context : null,
        reply: reply.length > 0 ? reply : null,
    };
}

/** @typedef {'executive'|'task-force-chair'|'pillar-chair'|'project-manager'|'member'|'admin'} ChatPersona */

/**
 * Resolve which chat template to use. Primary portal role is usually `ROLES.PROJECT_MANAGER`;
 * when the app role is member/chair/viewer, Project Manager directory role is detected from tokenRoles / accountRoles.
 * @param {string|null} appRole - Primary app role from auth
 * @param {object|null} [account] - Session account (`tokenRoles` from JWT or API `accountRoles` for Project Manager)
 * @returns {ChatPersona}
 */
export function resolveChatPersona(appRole, account) {
    if (!appRole) return 'member';
    if (appRole === ROLES.SUPER_ADMIN || appRole === ROLES.ADMIN) return 'admin';
    if (appRole === ROLES.EXECUTIVE) return 'executive';
    if (appRole === ROLES.TASK_FORCE_CHAIR) return 'task-force-chair';
    if (appRole === ROLES.PILLAR_CHAIR) return 'pillar-chair';
    if (appRole === ROLES.PROJECT_MANAGER) return 'project-manager';
    if (appRole === ROLES.MEMBER || appRole === ROLES.CHAIR || appRole === ROLES.VIEWER) {
        const names = Array.isArray(account?.tokenRoles) && account.tokenRoles.length
            ? account.tokenRoles
            : (account?.accountRoles?.map((ar) => ar?.role?.name).filter(Boolean) ?? []);
        if (names.some((n) => n === 'Project Manager')) return 'project-manager';
        return 'member';
    }
    return 'member';
}

/**
 * Group conversations for sidebar: "Groups" vs "Direct messages"
 * @param {import('../store/chatSlice').ConversationSummary[]} conversations
 * @returns {{ groups: import('../store/chatSlice').ConversationSummary[], direct: import('../store/chatSlice').ConversationSummary[] }}
 */
export function partitionConversationsByKind(conversations) {
    const groups = [];
    const direct = [];
    for (const c of conversations) {
        // Treat task-scoped group threads as “Specific Chat” items in the sidebar.
        if (c.kind === 'group' && c.scopeType === 'task') {
            direct.push(c);
            continue;
        }
        if (c.kind === 'group') groups.push(c);
        else direct.push(c);
    }
    return { groups, direct };
}

/**
 * Most recent activity first (same idea as iMessage / Slack per section).
 * @param {import('../store/chatSlice').ConversationSummary[]} list
 */
export function sortConversationsByRecentLastMessage(list) {
    if (!Array.isArray(list) || list.length < 2) {
        return Array.isArray(list) ? [...list] : [];
    }
    return [...list].sort((a, b) => {
        const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return String(b.id ?? '').localeCompare(String(a.id ?? ''));
    });
}

/**
 * Sidebar buckets: groups vs direct/specific, each sorted by recency.
 * @param {import('../store/chatSlice').ConversationSummary[]} conversations
 */
export function partitionAndSortConversationsByKind(conversations) {
    const { groups, direct } = partitionConversationsByKind(conversations);
    return {
        groups: sortConversationsByRecentLastMessage(groups),
        direct: sortConversationsByRecentLastMessage(direct),
    };
}

/**
 * @param {string} id
 * @param {string} label
 */
export function conversationSectionTitle(id, label) {
    return { id, label };
}
